import { useCallback, useMemo } from "react";

import { SITE_ROUTES } from "@/config/siteRoutes";
import {
  buildContactParagraphs,
  SITE_CONTACT_FALLBACK,
  useSiteContact,
} from "@/data/siteContact";
import { CmsManagedPage } from "@/pages/EditorialPages/CmsManagedPage";
import type { CmsContentPageData } from "@/pages/shared/cmsContent";

const fallbackBase: CmsContentPageData = {
  id: 8,
  slug: "contact",
  title: "Contact",
  intro:
    "Tell us what you want to create. Whether it’s a brand, a digital project, an event or a complete experience, we’ll help shape the right next step.",
  seoTitle: "Contact",
  searchDescription:
    "Contact VenueX for branding, digital presence, live event production and combined premium project briefs.",
  url: SITE_ROUTES.contact,
  bodyHtml: "",
  sections: [],
  paragraphs: [
    {
      html: "<p>VenueX is available for projects involving brand language, digital presence, event production or a combined system that has to move together without losing coherence.</p>",
      text: "VenueX is available for projects involving brand language, digital presence, event production or a combined system that has to move together without losing coherence.",
    },
    {
      html: "<p>If the project is not fully formed yet, the next useful move is to review the service system and portfolio routes first, then open the first conversation with the right context.</p>",
      text: "If the project is not fully formed yet, the next useful move is to review the service system and portfolio routes first, then open the first conversation with the right context.",
    },
  ],
};

export function ContactPage() {
  const contact = useSiteContact();
  const fallback = useMemo(
    () => ({
      ...fallbackBase,
      paragraphs: [
        fallbackBase.paragraphs[0],
        ...buildContactParagraphs(contact),
        ...fallbackBase.paragraphs.slice(1),
      ],
    }),
    [contact],
  );
  const transformPage = useCallback(
    (page: CmsContentPageData) => {
      const editorialParagraphs = page.paragraphs.filter(
        (paragraph) => !/^(Email|Phone|Landline|Mobile|Office):/i.test(paragraph.text.trim()),
      );
      const [leadParagraph, ...remainingParagraphs] = editorialParagraphs;

      return {
        ...page,
        paragraphs: [
          ...(leadParagraph ? [leadParagraph] : []),
          ...buildContactParagraphs(contact),
          ...remainingParagraphs,
        ],
      };
    },
    [contact],
  );

  return (
    <CmsManagedPage
      slug="contact"
      currentPath={SITE_ROUTES.contact}
      eyebrow="Enquiry"
      visualImage="/static/venuex/live/story-bright-rose.jpg"
      visualAlt="VenueX hospitality-led atmosphere for premium brand and event consultations"
      leadTitle="LET’S START WITH YOUR VISION."
      leadCopy="Share a few details about your project and we’ll be in touch to discuss the next steps."
      routeLinks={[]}
      primaryCtaLabel="START THE CONVERSATION"
      primaryCtaHref={`mailto:${contact.email || SITE_CONTACT_FALLBACK.email}`}
      secondaryCtaLabel=""
      secondaryCtaHref="#"
      privacyPolicyHref={SITE_ROUTES.privacyPolicy}
      contactForm
      minimalContent
      servicePanelTitle="EVERY PROJECT STARTS WITH A CONVERSATION."
      servicePanelNote=""
      servicePanelBody="From brand strategy and digital presence to event planning and execution, VenueX brings every element together with clarity, consistency and purpose.\n\nTell us where you are, what you need and what you want to achieve. We’ll help define the right direction for your project."
      servicePanelAccent="Founder-led. Purposefully delivered."
      hideServiceNumber
      hideServiceLabel
      formTitle="START YOUR PROJECT"
      formIntro="Share the essentials below so we can understand your project and prepare for a meaningful first conversation."
      formSubmitLabel="START THE CONVERSATION"
      formSuccessMessage="Thank you. Your enquiry has been received. We’ll be in touch shortly to discuss the next steps."
      fallback={fallback}
      transformPage={transformPage}
    />
  );
}
